import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Search, 
  Filter, 
  MapPin, 
  Star, 
  Heart, 
  Eye, 
  MessageCircle,
  Grid,
  List,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Video as VideoIcon,
  Image as ImageIcon
} from 'lucide-react';

const MarketplaceFeed = () => {
  const { user } = useAuth();
  const marketplace = useMarketplace();
  const { toast } = useToast();
  const { t } = useLanguage();
  
  // Safety checks to prevent runtime errors
  const { 
    searchResults = [], 
    categories = [], 
    loading = false, 
    error = null,
    searchProducts = () => {}, 
    addToFavorites = () => {}, 
    getProduct = () => {},
    fetchCategories = () => {}
  } = marketplace || {};

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [location, setLocation] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(20);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetailOpen, setProductDetailOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // Real recommendations require a backend endpoint (Phase 4) — no fabricated products.
  // Until then this is honestly empty (the search API currently returns recommendations: []).
  const [recommendedProducts] = useState([]);

  // Initialize categories and perform initial search
  useEffect(() => {
    try {
      if (fetchCategories && typeof fetchCategories === 'function') {
        fetchCategories();
      }
      handleSearch();
    } catch (error) {
      console.error('MarketplaceFeed initialization error:', error);
    }
  }, []);

  const handleSearch = async () => {
    const searchParams = {
      query: searchQuery,
      category_id: selectedCategory === 'all' ? null : selectedCategory,
      location: location,
      limit: productsPerPage,
      offset: (currentPage - 1) * productsPerPage
    };

    try {
      if (searchProducts && typeof searchProducts === 'function') {
        await searchProducts(searchParams);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: "Search Error", 
        description: "Failed to search products. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleProductClick = async (productId) => {
    try {
      if (getProduct && typeof getProduct === 'function') {
        const product = await getProduct(productId);
        setSelectedProduct(product);
        setProductDetailOpen(true);
      }
    } catch (error) {
      console.error('Product click error:', error);
      toast({
        title: "Error",
        description: "Failed to load product details.",
        variant: "destructive"
      });
    }
  };

  const handleAddToFavorites = async (productId, businessId) => {
    if (!user?.customer_id) {
      toast({
        title: "Login Required",
        description: "Please login as a customer to add favorites.",
        variant: "destructive"
      });
      return;
    }

    try {
      await addToFavorites(productId, businessId);
      toast({
        title: "Added to Favorites",
        description: "Product has been added to your favorites.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to add to favorites.",
        variant: "destructive"
      });
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    handleSearch();
  };

  // Final-batch fix — REAL client-side Sort + Price-range on the visible product page.
  // Honest: only products with a numeric price participate in price sort/filter; "contact for
  // price" products have no number, so they are excluded when a price bound is set and sort last.
  // Scope is the currently-loaded page (server pagination is unchanged) — no fabricated ordering.
  const _priceLow = (p) => {
    const pr = p?.pricing;
    if (!pr) return null;
    if (pr.type === 'single') { const v = Number(pr.value); return Number.isFinite(v) ? v : null; }
    if (pr.type === 'range') { const v = Number(pr.min); return Number.isFinite(v) ? v : null; }
    return null;
  };
  const _priceHigh = (p) => {
    const pr = p?.pricing;
    if (!pr) return null;
    if (pr.type === 'single') { const v = Number(pr.value); return Number.isFinite(v) ? v : null; }
    if (pr.type === 'range') { const v = Number(pr.max); return Number.isFinite(v) ? v : null; }
    return null;
  };

  const visibleProducts = useMemo(() => {
    let list = Array.isArray(searchResults) ? [...searchResults] : [];

    // Price-range filter (only when a bound is provided). Products without a numeric price are
    // dropped when a bound is active — we never guess a price.
    const fMin = priceRange.min !== '' && Number.isFinite(Number(priceRange.min)) ? Number(priceRange.min) : null;
    const fMax = priceRange.max !== '' && Number.isFinite(Number(priceRange.max)) ? Number(priceRange.max) : null;
    if (fMin !== null || fMax !== null) {
      list = list.filter((p) => {
        const lo = _priceLow(p);
        const hi = _priceHigh(p);
        if (lo === null && hi === null) return false;         // no numeric price → cannot match a range
        if (fMin !== null && (hi === null || hi < fMin)) return false;
        if (fMax !== null && (lo === null || lo > fMax)) return false;
        return true;
      });
    }

    // Sort
    if (sortBy === 'price-low') {
      list.sort((a, b) => {
        const av = _priceLow(a), bv = _priceLow(b);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;                            // no-price sorts last
        if (bv === null) return -1;
        return av - bv;
      });
    } else if (sortBy === 'price-high') {
      list.sort((a, b) => {
        const av = _priceHigh(a), bv = _priceHigh(b);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return bv - av;
      });
    } else if (sortBy === 'newest') {
      // Sort by created_at desc when the field exists; otherwise preserve server order.
      if (list.some((p) => p?.created_at)) {
        list.sort((a, b) => String(b?.created_at || '').localeCompare(String(a?.created_at || '')));
      }
    }
    // NOTE: no "Most Popular" branch — there is no popularity/view-count data, so that option
    // was removed from the control rather than faking an order.
    return list;
  }, [searchResults, sortBy, priceRange.min, priceRange.max]);

  const renderProductCard = (product) => (
    <Card key={product.id} className="cursor-pointer hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-4">
        {/* Product Image */}
        <div className="relative aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
          {product.images && product.images.length > 0 ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik05IDEwQzguNDQ3NzIgMTAgOCAxMC40NDc3IDggMTFDOCAxMS41NTIzIDguNDQ3NzIgMTIgOSAxMkM5LjU1MjI4IDEyIDEwIDExLjU1MjMgMTAgMTFDMTAgMTAuNDQ3NyA5LjU1MjI4IDEwIDkgMTBaIiBmaWxsPSIjOUI5QkEwIi8+CjxwYXRoIGQ9Ik0yMSA1SDE5TDE3IDNIMTBMOCA1SDNDMi40NDc3MiA1IDIgNS40NDc3MiAyIDZWMThDMiAxOC41NTIzIDIuNDQ3NzIgMTkgMyAxOUgyMUMyMS41NTIzIDE5IDIyIDE4LjU1MjMgMjIgMThWNkMyMiA1LjQ0NzcyIDIxLjU1MjMgNSAyMSA1WiIgc3Ryb2tlPSIjOUI5QkEwIiBzdHJva2Utd2lkdGg9IjEuNSIgZmlsbD0ibm9uZSIvPgo8L3N2Zz4K';
              }}
            />
          ) : (
            <ImageIcon className="h-12 w-12 text-gray-400" />
          )}
          {product.video && (
            <div className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1.5" title={t('fin.mf.includesVideo')}>
              <VideoIcon className="h-3.5 w-3.5" />
            </div>
          )}
        </div>

        {/* Product Info */}
        <div onClick={() => handleProductClick(product.id)}>
          <h3 className="font-semibold text-sm mb-1 line-clamp-2">{product.name}</h3>
          
          {/* Star Rating — real rating only; no fabricated fallback (aggregate ratings land in Phase 4) */}
          <div className="flex items-center gap-1 mb-1">
            {product.rating ? (
              <>
                <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
                <span className="text-xs text-gray-500">{product.rating}</span>
              </>
            ) : (
              <span className="text-xs text-gray-400">{t('fin.mf.noRatings')}</span>
            )}
          </div>

          {/* Price */}
          {product.pricing && (
            <div className="mb-2">
              {product.pricing.type === 'single' && (
                <span className="text-lg font-bold text-blue-600">
                  ${product.pricing.value}
                </span>
              )}
              {product.pricing.type === 'range' && (
                <span className="text-lg font-bold text-blue-600">
                  ${product.pricing.min} - ${product.pricing.max}
                </span>
              )}
              {product.pricing.type === 'contact' && (
                <span className="text-sm text-gray-600 font-medium">{t('pg.mkt.contactForPrice')}</span>
              )}
            </div>
          )}

          {/* Location */}
          {product.location && (
            <div className="flex items-center text-xs text-gray-500 mb-2">
              <MapPin className="h-3 w-3 mr-1" />
              <span className="truncate">{product.location.address}</span>
            </div>
          )}

          {/* Business Name */}
          <p className="text-xs text-gray-600 mb-2">{product.business_name || 'Business Name'}</p>

          {/* Tags */}
          {product.tags && product.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {product.tags.slice(0, 2).map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {product.tags.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{product.tags.length - 2}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mt-3 pt-3 border-t">
          <div className="flex items-center space-x-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleAddToFavorites(product.id, product.business_id);
              }}
              className="h-8 px-2"
            >
              <Heart className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2"
              title={t('fin.mf.viewDetails')}
              onClick={(e) => {
                e.stopPropagation();
                handleProductClick(product.id);
              }}
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
          
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleProductClick(product.id);
            }}
            className="h-8"
          >
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderProductList = (product) => (
    <Card key={product.id} className="cursor-pointer hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-4">
        <div className="flex space-x-4">
          {/* Product Image */}
          <div className="relative w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            {product.images && product.images.length > 0 ? (
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <ImageIcon className="h-8 w-8 text-gray-400" />
            )}
            {product.video && (
              <div className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1" title={t('fin.mf.includesVideo')}>
                <VideoIcon className="h-3 w-3" />
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="flex-1 min-w-0" onClick={() => handleProductClick(product.id)}>
            <h3 className="font-semibold text-base mb-1">{product.name}</h3>
            <p className="text-sm text-gray-600 line-clamp-2 mb-2">{product.description}</p>
            
            {/* Price and Location */}
            <div className="flex items-center justify-between mb-2">
              {product.pricing && (
                <div>
                  {product.pricing.type === 'single' && (
                    <span className="text-xl font-bold text-blue-600">${product.pricing.value}</span>
                  )}
                  {product.pricing.type === 'contact' && (
                    <span className="text-sm text-gray-600 font-medium">{t('pg.mkt.contactForPrice')}</span>
                  )}
                </div>
              )}
              
              {product.location && (
                <div className="flex items-center text-sm text-gray-500">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span>{product.location.address}</span>
                </div>
              )}
            </div>

            {/* Tags */}
            {product.tags && product.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {product.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col justify-between items-end">
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToFavorites(product.id, product.business_id);
                }}
              >
                <Heart className="h-4 w-4" />
              </Button>
            </div>
            
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleProductClick(product.id);
              }}
            >
              View Details
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Search Bar */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={t('pg.mkt.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
            <Button variant="outline" onClick={() => setFilterOpen(true)}>
              <Filter className="h-4 w-4 mr-2" />
              {t('common.filters')}
            </Button>
          </div>

          {/* Quick Filters */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Select value={selectedCategory || undefined} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={t('pg.mkt.allCategories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('pg.mkt.allCategories')}</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                placeholder={t('pg.mkt.location')}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-48"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Error Banner — uses destructured `error` from marketplace context */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <span className="font-medium">{t('fin.mf.marketplaceError')}</span> {typeof error === 'string' ? error : t('fin.mf.somethingWrong')}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Recommended for You */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">{t('pg.mkt.recommended')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {recommendedProducts.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">{t('pg.mkt.noRecommendations')}</p>
                ) : recommendedProducts.map((product) => (
                  <div key={product.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      <ImageIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{product.name}</p>
                      <p className="text-xs text-gray-500">{product.category}</p>
                      <p className="text-sm font-semibold text-blue-600">${product.price}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Categories */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('pg.mkt.browseCategories')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {categories.slice(0, 8).map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(category.id);
                        handleSearch();
                      }}
                      className="w-full text-left p-2 text-sm hover:bg-gray-100 rounded flex justify-between items-center"
                    >
                      <span>{category.name}</span>
                      {category.sub_categories && (
                        <Badge variant="secondary" className="text-xs">
                          {category.sub_categories.length}
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold">
                  {searchQuery ? t('pg.mkt.searchResultsFor', { query: searchQuery }) : t('pg.mkt.allProducts')}
                </h2>
                <p className="text-sm text-gray-600">
                  {visibleProducts.length} {t('pg.mkt.productsFound')}
                </p>
              </div>

              <Select value={sortBy || undefined} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t('pg.mkt.newestFirst')}</SelectItem>
                  <SelectItem value="price-low">{t('pg.mkt.priceLowHigh')}</SelectItem>
                  <SelectItem value="price-high">{t('pg.mkt.priceHighLow')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Products Grid/List */}
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : searchResults.length > 0 ? (
              <>
                {visibleProducts.length > 0 ? (
                  <div className={`${
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                      : 'space-y-4'
                  } mb-8`}>
                    {visibleProducts.map((product) =>
                      viewMode === 'grid'
                        ? renderProductCard(product)
                        : renderProductList(product)
                    )}
                  </div>
                ) : (
                  // Honest: server returned products for this page, but the price filter removed
                  // all of them. Keep pagination so the user can navigate or clear the filter.
                  <div className="text-center py-8 mb-4 text-sm text-gray-500">
                    {t('pg.mkt.noPriceMatch')}
                  </div>
                )}

                {/* Pagination */}
                <div className="flex items-center justify-center space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  <span className="text-sm text-gray-600">
                    {t('pg.mkt.page')} {currentPage}
                  </span>
                  
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={searchResults.length < productsPerPage}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('pg.mkt.noProductsFound')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('pg.mkt.adjustSearch')}
                </p>
                <Button onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('');
                  setLocation('');
                  handleSearch();
                }}>
                  {t('pg.mkt.clearFilters')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Detail Dialog */}
      <Dialog open={productDetailOpen} onOpenChange={setProductDetailOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedProduct?.name}</DialogTitle>
            <DialogDescription>
              {t('pg.mkt.productDetailsInfo')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              {/* Product Images */}
              {selectedProduct.images && selectedProduct.images.length > 0 && (
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={selectedProduct.images[0]}
                    alt={selectedProduct.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Product Video (single, optional — mp4/mov signed URL) */}
              {selectedProduct.video && (
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <video
                    src={selectedProduct.video}
                    controls
                    className="w-full h-full object-contain"
                  />
                </div>
              )}

              {/* Product Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">{t('pg.mkt.description')}</h3>
                  <p className="text-gray-600">{selectedProduct.description}</p>
                </div>
                
                <div className="space-y-4">
                  {/* Price */}
                  {selectedProduct.pricing && (
                    <div>
                      <h4 className="font-medium mb-1">{t('pg.mkt.price')}</h4>
                      {selectedProduct.pricing.type === 'single' && (
                        <p className="text-2xl font-bold text-blue-600">
                          ${selectedProduct.pricing.value}
                        </p>
                      )}
                      {selectedProduct.pricing.type === 'contact' && (
                        <p className="text-gray-600">{t('pg.mkt.contactForPrice')}</p>
                      )}
                    </div>
                  )}

                  {/* Location */}
                  {selectedProduct.location && (
                    <div>
                      <h4 className="font-medium mb-1">{t('pg.mkt.location')}</h4>
                      <p className="text-gray-600">{selectedProduct.location.address}</p>
                    </div>
                  )}

                  {/* Tags */}
                  {selectedProduct.tags && selectedProduct.tags.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">{t('pg.mkt.tags')}</h4>
                      <div className="flex flex-wrap gap-1">
                        {selectedProduct.tags.map((tag, index) => (
                          <Badge key={index} variant="secondary">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4 pt-4 border-t">
                <Button
                  onClick={() => handleAddToFavorites(selectedProduct.id, selectedProduct.business_id)}
                  variant="outline"
                  className="flex-1"
                >
                  <Heart className="h-4 w-4 mr-2" />
                  {t('pg.mkt.addToFavorites')}
                </Button>
                
                <Button
                  className="flex-1"
                  onClick={() => toast({
                    title: t('pg.mkt.contactBusiness'),
                    description: t('pg.mkt.contactBusinessSoon'),
                  })}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {t('pg.mkt.contactBusiness')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('pg.mkt.filterProducts')}</DialogTitle>
            <DialogDescription>
              {t('pg.mkt.refineSearch')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Price Range */}
            <div>
              <h4 className="font-medium mb-2">{t('pg.mkt.priceRange')}</h4>
              <div className="flex space-x-2">
                <Input
                  placeholder={t('pg.mkt.min')}
                  type="number"
                  value={priceRange.min}
                  onChange={(e) => setPriceRange(prev => ({...prev, min: e.target.value}))}
                />
                <Input
                  placeholder={t('pg.mkt.max')}
                  type="number"
                  value={priceRange.max}
                  onChange={(e) => setPriceRange(prev => ({...prev, max: e.target.value}))}
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <h4 className="font-medium mb-2">{t('pg.mkt.location')}</h4>
              <Input
                placeholder={t('fin.mf.enterLocation')}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>

            {/* Category */}
            <div>
              <h4 className="font-medium mb-2">{t('pg.mkt.category')}</h4>
              <Select value={selectedCategory || undefined} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder={t('pg.mkt.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('pg.mkt.allCategories')}</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('');
                  setLocation('');
                  setPriceRange({ min: '', max: '' });
                }}
                className="flex-1"
              >
                {t('pg.mkt.clearAll')}
              </Button>
              <Button
                onClick={() => {
                  handleSearch();
                  setFilterOpen(false);
                }}
                className="flex-1"
              >
                {t('pg.mkt.applyFilters')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketplaceFeed;